import { useMediaQuery, useTheme } from '@mui/material'

/**
 * Custom hook for responsive design
 * Provides semantic device type flags and screen size information
 *
 * @returns {Object} Device flags and current breakpoint
 */
export function useResponsive() {
  const theme = useTheme()

  // Breakpoint definitions
  const isXs = useMediaQuery(theme.breakpoints.only('xs')) // < 600px
  const isSm = useMediaQuery(theme.breakpoints.only('sm')) // 600px - 900px
  const isMd = useMediaQuery(theme.breakpoints.only('md')) // 900px - 1200px
  const isLg = useMediaQuery(theme.breakpoints.only('lg')) // 1200px - 1536px
  const isXl = useMediaQuery(theme.breakpoints.only('xl')) // >= 1536px

  // Up and down queries
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm')) // < 600px
  const isMdDown = useMediaQuery(theme.breakpoints.down('md')) // < 900px
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg')) // < 1200px

  const isSmUp = useMediaQuery(theme.breakpoints.up('sm')) // >= 600px
  const isMdUp = useMediaQuery(theme.breakpoints.up('md')) // >= 900px
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg')) // >= 1200px

  // Semantic device types
  const isMobile = isXs // < 600px
  const isTablet = isSm || isMd // 600px - 1200px
  const isDesktop = isLg || isXl // >= 1200px

  // Screen orientation
  const isPortrait = useMediaQuery('(orientation: portrait)')
  const isLandscape = useMediaQuery('(orientation: landscape)')

  // Current breakpoint
  let currentBreakpoint = 'xs'
  if (isXl) currentBreakpoint = 'xl'
  else if (isLg) currentBreakpoint = 'lg'
  else if (isMd) currentBreakpoint = 'md'
  else if (isSm) currentBreakpoint = 'sm'

  return {
    // Breakpoint flags
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,

    // Up/down queries
    isSmDown,
    isMdDown,
    isLgDown,
    isSmUp,
    isMdUp,
    isLgUp,

    // Semantic device types
    isMobile,
    isTablet,
    isDesktop,

    // Orientation
    isPortrait,
    isLandscape,

    // Current breakpoint name
    currentBreakpoint,
  }
}
